import { useEffect, useRef, useState } from "react";
import { Button, Row, Col, Dropdown, Layout, MenuProps, Typography } from "@version2/ui-package";
import { useLocation } from "react-router-dom";
import { pages } from "../../../../Consts/config";
import { MenuOutlined } from "@ant-design/icons";
import { isValidUser, listenToAuthState } from "../../../../Services/firebase/auth";
import { User } from "firebase/auth";
import { Page } from "src/Consts/config/pages";
import { APP_FEATURES } from "src/Consts/features";
import { useFeatureAccess } from "src/Services/feature_access";

const { Header } = Layout;
const { Title } = Typography;

const FullScreenHeader = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const [authed, setAuthed] = useState(null as boolean | null);
  const { isFeatureEnabled } = useFeatureAccess();
  const isAddOnsEnabled = isFeatureEnabled(APP_FEATURES.ADD_ONS);

  const stylePrefix = "full_screen__header";

  const onChangeAuthState = (user: User | null) => setAuthed(isValidUser(user));

  useEffect(() => {
    listenToAuthState(onChangeAuthState);
  }, []);

  return (
    <div>
      <Header className="full_screen__header__background" />
      <Header className="full_screen__header">
        <div className={`${stylePrefix}__menu`}>
          <FullScreenHeaderMobile authed={authed} />
        </div>
        <Row justify="space-between" className={`${stylePrefix}__nav_bar`}>
          <Col>
            <HeaderButton
              page={pages.HOME_PAGE}
              text="Home"
              pathname={pathname}
            />
            <HeaderButton page={pages.PRICING_PAGE} pathname={pathname} />
            {isAddOnsEnabled && (
              <HeaderButton
                page={pages.EXPLORE_ADD_ONS_PAGE}
                pathname={pathname}
              />
            )}
          </Col>
          <Col>
            {authed !== null && (
              <HeaderButton
                text={authed ? "Go to Dashboard" : "Sign In"}
                pathname={pathname}
                page={authed ? pages.APP_PAGE : pages.SIGN_IN_PAGE}
                onClick={() => (window.location.href = authed ? pages.APP_PAGE.url : pages.SIGN_IN_PAGE.url)}
              />
            )}
          </Col>
        </Row>
      </Header>
    </div>
  );
};

type HeaderButtonProps = {
  page?: Page;
  text?: string;
  pathname: string;
  onClick?: () => void;
};

const HeaderButton = ({ page, text, pathname, onClick }: HeaderButtonProps) => {
  return (
    <Button
      type="text"
      href={onClick ? undefined : page?.url}
      onClick={onClick}
      style={{
        fontWeight: pathname === page?.url ? "bold" : "normal",
      }}
    >
      {text || page?.name}
    </Button>
  );
};

type FullScreenHeaderMobileProps = {
  authed: boolean | null;
};

const FullScreenHeaderMobile = ({ authed }: FullScreenHeaderMobileProps) => {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navigationTrigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 768px)");
    const closeAtDesktop = () => {
      if (desktop.matches) setNavigationOpen(false);
    };
    desktop.addEventListener("change", closeAtDesktop);
    return () => desktop.removeEventListener("change", closeAtDesktop);
  }, []);

  useEffect(() => {
    if (!navigationOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setNavigationOpen(false);
        navigationTrigger.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [navigationOpen]);

  const { isFeatureEnabled } = useFeatureAccess();
  const isAddOnsEnabled = isFeatureEnabled(APP_FEATURES.ADD_ONS);

  const menuItems: MenuProps["items"] = [
    {
      label: pages.HOME_PAGE.name,
      key: pages.HOME_PAGE.url,
      onClick: () => (window.location.href = pages.HOME_PAGE.url),
    },
    {
      label: pages.PRICING_PAGE.name,
      key: pages.PRICING_PAGE.url,
      onClick: () => (window.location.href = pages.PRICING_PAGE.url),
    },
    ...(isAddOnsEnabled
      ? [
          {
            label: pages.EXPLORE_ADD_ONS_PAGE.name,
            key: pages.EXPLORE_ADD_ONS_PAGE.url,
            onClick: () => (window.location.href = pages.EXPLORE_ADD_ONS_PAGE.url),
          },
        ]
      : []),
    {
      label: authed === true ? "Go to Dashboard" : "Sign In",
      key: "sign_in",
      onClick: () => (window.location.href = authed ? pages.APP_PAGE.url : pages.SIGN_IN_PAGE.url),
    },
  ];

  return (
    <Row align="middle" justify="space-between">
      <Col>
        <div className="full_screen__header__mobile">
          <Dropdown
            menu={{ items: menuItems, onClick: () => setNavigationOpen(false) }}
            open={navigationOpen}
            onOpenChange={setNavigationOpen}
            classNames={{ root: "full_screen__navigation_menu" }}
            placement="bottomLeft"
            trigger={["click"]}
          >
            <Button
              type="text"
              ref={navigationTrigger}
              aria-label="Open navigation"
              aria-haspopup="menu"
              aria-expanded={navigationOpen}
              className="full_screen__header__mobile__icon"
              icon={<MenuOutlined rev={undefined} />}
            />
          </Dropdown>
        </div>
      </Col>
      <Col>
        <Title
          level={5}
          className="full_screen__header__mobile__title"
        >
          {pages.urlToPage(window.location.pathname)?.name}
        </Title>
      </Col>
      <Col
        className="full_screen__header__mobile__spacer"
       />
    </Row>
  );
};

export default FullScreenHeader;
